import { memo } from 'react';
import {
    Bar,
    BarChart,
    BarStack,
    CartesianGrid,
    ReferenceArea,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import type { HistoryBucket } from '../../../services/dmMonitoringOverviewHistory';
import { AXIS_TICK, CHART_RESIZE_DEBOUNCE_MS, HOVER_CURSOR } from '../../../utils/chartFormatting';
import { ACTIVITY_CHART_HEIGHT_PX } from './ActivityChart';
import { BACKLOG_KEYS, type BacklogRow, type BacklogSpan } from './backlogRows';
import { BACKLOG_LEGEND, UNRELIABLE_SHADE } from './overviewLegend';
import { bucketLabel } from './overviewSeries';

const CHART_MARGIN = { top: 8, right: 4, left: -16, bottom: 0 } as const;
const STACK_RADIUS: [number, number, number, number] = [3, 3, 0, 0];
const MAX_BAR_PX = 28;
const MIN_TICK_GAP_PX = 16;
const MIN_MESSAGE_CAP_PX = 3;
const NO_DATA = 'No data';

function formatCount(value: number | null): string {
    return value === null ? NO_DATA : value.toLocaleString();
}

function TooltipLine({ label, value, color }: { label: string; value: string; color: string }) {
    return (
        <div className="flex items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded-[2px]" style={{ background: color }} />
            <span className="flex-1 text-[11px] text-gray-500">{label}</span>
            <span className="text-[11px] font-semibold tabular-nums text-gray-900">{value}</span>
        </div>
    );
}

interface BacklogTooltipProps {
    totalLabel: string;
    active?: boolean;
    payload?: { payload: BacklogRow }[];
}

function BacklogTooltip({ totalLabel, active, payload }: BacklogTooltipProps) {
    const row = active ? payload?.[0]?.payload : undefined;
    if (!row) return null;
    return (
        <div className="min-w-[140px] rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 shadow-lg">
            <p className="mb-1.5 text-xs font-bold text-gray-800">{row.label}</p>
            <div className="flex flex-col gap-1">
                {BACKLOG_KEYS.map((key) => (
                    <TooltipLine
                        key={key}
                        label={BACKLOG_LEGEND[key].label}
                        value={formatCount(row[key])}
                        color={BACKLOG_LEGEND[key].color}
                    />
                ))}
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-2 border-t border-gray-100 pt-1.5">
                <span className="text-[11px] font-semibold text-gray-600">{totalLabel}</span>
                <span className="text-[11px] font-bold tabular-nums text-gray-900">
                    {formatCount(row.total)}
                </span>
            </div>
        </div>
    );
}

// recharts passes the stack's running top, so the segment's own count is looked up by index.
function messageCapSize(rows: readonly BacklogRow[]) {
    return (_top: number | null | undefined, index: number) =>
        rows[index]?.messages ? MIN_MESSAGE_CAP_PX : 0;
}

interface OpenBacklogChartProps {
    rows: readonly BacklogRow[];
    bucket: HistoryBucket;
    unreliable: BacklogSpan | null;
    totalLabel: string;
}

export const OpenBacklogChart = memo(function OpenBacklogChart({
    rows,
    bucket,
    unreliable,
    totalLabel
}: OpenBacklogChartProps) {
    return (
        <ResponsiveContainer
            width="100%"
            height={ACTIVITY_CHART_HEIGHT_PX}
            debounce={CHART_RESIZE_DEBOUNCE_MS}
            className="overflow-x-clip"
        >
            <BarChart data={[...rows]} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                    dataKey="bucketStart"
                    tickFormatter={(value: string) => bucketLabel(value, bucket)}
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    tickMargin={6}
                    interval="preserveStartEnd"
                    minTickGap={MIN_TICK_GAP_PX}
                />
                <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} allowDecimals={false} />
                {unreliable && (
                    <ReferenceArea x1={unreliable.x1} x2={unreliable.x2} {...UNRELIABLE_SHADE} />
                )}
                <Tooltip
                    cursor={HOVER_CURSOR}
                    content={<BacklogTooltip totalLabel={totalLabel} />}
                    filterNull={false}
                    isAnimationActive={false}
                />
                {/* no stackId: recharts names clipPaths after it, so a shared id clips every chart to the first */}
                <BarStack radius={STACK_RADIUS}>
                    {BACKLOG_KEYS.map((key) => (
                        <Bar
                            key={key}
                            dataKey={key}
                            name={BACKLOG_LEGEND[key].label}
                            fill={BACKLOG_LEGEND[key].color}
                            maxBarSize={MAX_BAR_PX}
                            minPointSize={key === 'messages' ? messageCapSize(rows) : 0}
                            isAnimationActive={false}
                        />
                    ))}
                </BarStack>
            </BarChart>
        </ResponsiveContainer>
    );
});
